import { SLUG_PATTERN } from "../content/slug.ts";
import { ITEM_ID_PATTERN, MAX_ITEM_STACK } from "../rules/types.ts";
import { SEASONS, type Season } from "../time/game-clock.ts";
import { DEFAULT_DIE_AFTER, DEFAULT_WILT_AFTER, MAX_CROP_HOURS, MAX_CROP_STAGES, WATER_NEEDS } from "./crop-types.ts";
import type { CountRange, CropContext, CropDefinition, CropFrame, CropLook, CropProblem, CropStage, CropYield, QualityWeights, WaterNeed } from "./crop-types.ts";

/**
 * The readers behind `validateCrop`: each takes one parsed field, adds every problem it finds to the list and returns the
 * field with its defaults filled, or null when it is not sound. Pure.
 */

type Raw = Record<string, unknown>;

const TINT_PATTERN = /^#[0-9a-fA-F]{6}$/;

/**
 * Whether a value is a plain object.
 *
 * @param value The value.
 * @returns True for an object that is not null or a list.
 */
export function isRecord(value: unknown): value is Raw {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Whether a value is a whole number from `min` to `max`.
 *
 * @param value The value.
 * @param min The least allowed.
 * @param max The most allowed.
 * @returns True for a whole number in range.
 */
export function isWhole(value: unknown, min: number, max: number): value is number {
  return typeof value === "number" && Number.isInteger(value) && value >= min && value <= max;
}

/**
 * Names every key of an object that is not in the allowed list.
 *
 * @param record The object.
 * @param allowed The keys it may hold.
 * @param path The path of the object, or "" for the crop itself.
 * @param problems The list to add to.
 */
export function refuseUnknown(record: Raw, allowed: readonly string[], path: string, problems: CropProblem[]): void {
  for (const key of Object.keys(record)) {
    if (!allowed.includes(key)) problems.push({ field: path === "" ? key : `${path}.${key}`, message: "is not a field here" });
  }
}

/**
 * Reads a frame, checking its shape and that the library has it.
 *
 * @param value The frame as parsed.
 * @param path The path of the frame.
 * @param context What frames exist.
 * @param problems The list to add to.
 * @returns The frame, or null when it is not sound.
 */
function readFrame(value: unknown, path: string, context: CropContext, problems: CropProblem[]): CropFrame | null {
  if (!isRecord(value) || typeof value.tileset !== "string" || value.tileset === "" || !isWhole(value.tile, 0, 0x7fffffff) || Object.keys(value).length !== 2) {
    problems.push({ field: path, message: "must be { tileset, tile } with a whole tile number" });
    return null;
  }
  const frame: CropFrame = { tileset: value.tileset, tile: value.tile };
  if (!context.hasFrame(frame)) {
    problems.push({ field: path, message: "is not a frame the studio has published" });
    return null;
  }
  return frame;
}

/**
 * Reads a count range, checking each end and their order.
 *
 * @param value The range as parsed.
 * @param path The path of the range.
 * @param least The least a count may be.
 * @param problems The list to add to.
 * @returns The range, or null when it is not sound.
 */
function readRange(value: unknown, path: string, least: number, problems: CropProblem[]): CountRange | null {
  if (!isRecord(value)) {
    problems.push({ field: path, message: "must be { min, max }" });
    return null;
  }
  let sound = true;
  for (const end of ["min", "max"] as const) {
    if (!isWhole(value[end], least, MAX_ITEM_STACK)) {
      problems.push({ field: `${path}.${end}`, message: `must be a whole number from ${least} to ${MAX_ITEM_STACK}` });
      sound = false;
    }
  }
  if (!sound) return null;
  const range = { min: value.min as number, max: value.max as number };
  if (range.min > range.max) {
    problems.push({ field: path, message: "min must not be above max" });
    return null;
  }
  return range;
}

/**
 * Checks that an item exists and is of the kind a crop needs of it.
 *
 * @param id The item id.
 * @param kind The kind it must be.
 * @param path The path of the field.
 * @param context What items exist.
 * @param problems The list to add to.
 */
export function checkItem(id: unknown, kind: string, path: string, context: CropContext, problems: CropProblem[]): void {
  if (typeof id !== "string" || !ITEM_ID_PATTERN.test(id)) {
    problems.push({ field: path, message: "must be an item id" });
    return;
  }
  const found = context.itemKind(id);
  if (found === undefined) problems.push({ field: path, message: "is not an item" });
  else if (found !== kind) problems.push({ field: path, message: "is an item of the wrong kind" });
}

/**
 * Reads the stage list.
 *
 * @param value The stages as parsed.
 * @param context What frames exist.
 * @param problems The list to add to.
 * @returns The stages, or null when the list or any stage is not sound.
 */
export function readStages(value: unknown, context: CropContext, problems: CropProblem[]): CropStage[] | null {
  if (!Array.isArray(value) || value.length < 2 || value.length > MAX_CROP_STAGES) {
    problems.push({ field: "stages", message: `must be a list of 2 to ${MAX_CROP_STAGES} stages` });
    return null;
  }
  const stages: CropStage[] = [];
  let sound = true;
  value.forEach((entry: unknown, index) => {
    const path = `stages[${index}]`;
    if (!isRecord(entry)) {
      problems.push({ field: path, message: "must be { frame, hours, water }" });
      sound = false;
      return;
    }
    refuseUnknown(entry, ["frame", "hours", "water"], path, problems);
    const frame = readFrame(entry.frame, `${path}.frame`, context, problems);
    if (!isWhole(entry.hours, 0, MAX_CROP_HOURS)) problems.push({ field: `${path}.hours`, message: `must be a whole number of game hours up to ${MAX_CROP_HOURS}` });
    if (!(WATER_NEEDS as readonly unknown[]).includes(entry.water)) problems.push({ field: `${path}.water`, message: "must be none, normal or thirsty" });
    if (frame === null || !isWhole(entry.hours, 0, MAX_CROP_HOURS) || !(WATER_NEEDS as readonly unknown[]).includes(entry.water)) sound = false;
    else stages.push({ frame, hours: entry.hours, water: entry.water as WaterNeed });
  });
  return sound ? stages : null;
}

/**
 * Checks that every stage before the ripe one lasts at least one game hour.
 *
 * @param stages The stages.
 * @param ripe The index of the ripe stage.
 * @param problems The list to add to.
 */
export function checkStageHours(stages: readonly CropStage[], ripe: number, problems: CropProblem[]): void {
  stages.forEach((stage, index) => {
    if (index < ripe && stage.hours < 1) problems.push({ field: `stages[${index}].hours`, message: "must be at least 1 game hour before the ripe stage" });
  });
}

/**
 * Reads the ripe stage's index: the stage list's last stage unless the crop names one.
 *
 * @param value The `harvestAt` as parsed, or undefined.
 * @param stageCount The number of stages.
 * @param problems The list to add to.
 * @returns The index, or null when it is not sound.
 */
export function readHarvestAt(value: unknown, stageCount: number, problems: CropProblem[]): number | null {
  if (value === undefined) return stageCount - 1;
  if (!isWhole(value, 1, stageCount - 1)) {
    problems.push({ field: "harvestAt", message: `must be a stage after the first, from 1 to ${stageCount - 1}` });
    return null;
  }
  return value;
}

/**
 * Reads how a wilted or dead crop looks.
 *
 * @param value The look as parsed, or undefined.
 * @param path The path of the field.
 * @param fallbackTint The tint to use when the crop says none.
 * @param context What frames exist.
 * @param problems The list to add to.
 * @returns The look, or null when it is not sound.
 */
export function readLook(value: unknown, path: string, fallbackTint: string, context: CropContext, problems: CropProblem[]): CropLook | null {
  if (value === undefined) return { tint: fallbackTint };
  if (!isRecord(value) || Object.keys(value).length !== 1 || (!("tint" in value) && !("frame" in value))) {
    problems.push({ field: path, message: "must be exactly one of { tint } or { frame }" });
    return null;
  }
  if ("tint" in value) {
    if (typeof value.tint === "string" && TINT_PATTERN.test(value.tint)) return { tint: value.tint };
    problems.push({ field: `${path}.tint`, message: "must be a colour like #8a6a3a" });
    return null;
  }
  const frame = readFrame(value.frame, `${path}.frame`, context, problems);
  return frame === null ? null : { frame };
}

/**
 * Reads the drought thresholds.
 *
 * @param value The drought as parsed, or undefined.
 * @param problems The list to add to.
 * @returns The thresholds, or null when they are not sound.
 */
export function readDrought(value: unknown, problems: CropProblem[]): { wiltAfter: number; dieAfter: number } | null {
  if (value === undefined) return { wiltAfter: DEFAULT_WILT_AFTER, dieAfter: DEFAULT_DIE_AFTER };
  if (!isRecord(value)) {
    problems.push({ field: "drought", message: "must be { wiltAfter, dieAfter }" });
    return null;
  }
  refuseUnknown(value, ["wiltAfter", "dieAfter"], "drought", problems);
  let sound = true;
  for (const key of ["wiltAfter", "dieAfter"] as const) {
    if (!isWhole(value[key], 1, MAX_CROP_HOURS)) {
      problems.push({ field: `drought.${key}`, message: `must be a whole number of game hours from 1 to ${MAX_CROP_HOURS}` });
      sound = false;
    }
  }
  if (!sound) return null;
  const drought = { wiltAfter: value.wiltAfter as number, dieAfter: value.dieAfter as number };
  if (drought.wiltAfter >= drought.dieAfter) {
    problems.push({ field: "drought", message: "wiltAfter must be less than dieAfter" });
    return null;
  }
  return drought;
}

/**
 * Reads the seasons a crop may be planted in.
 *
 * @param value The seasons as parsed, or undefined.
 * @param problems The list to add to.
 * @returns The seasons (empty means any), or null when they are not sound.
 */
export function readSeasons(value: unknown, problems: CropProblem[]): Season[] | null {
  if (value === undefined) return [];
  if (!Array.isArray(value)) {
    problems.push({ field: "seasons", message: "must be a list of seasons" });
    return null;
  }
  let sound = true;
  value.forEach((season: unknown, index) => {
    if (!(SEASONS as readonly unknown[]).includes(season)) {
      problems.push({ field: `seasons[${index}]`, message: "is not a season of the clock" });
      sound = false;
    }
  });
  if (sound && new Set(value).size !== value.length) {
    problems.push({ field: "seasons", message: "must not repeat a season" });
    sound = false;
  }
  return sound ? (value as Season[]) : null;
}

/**
 * Reads the regions a crop may grow in.
 *
 * @param value The regions as parsed, or undefined.
 * @param problems The list to add to.
 * @returns The regions (empty means anywhere), or null when they are not sound.
 */
export function readRegions(value: unknown, problems: CropProblem[]): string[] | null {
  if (value === undefined) return [];
  if (!Array.isArray(value)) {
    problems.push({ field: "regions", message: "must be a list of region ids" });
    return null;
  }
  let sound = true;
  value.forEach((region: unknown, index) => {
    if (typeof region !== "string" || !SLUG_PATTERN.test(region)) {
      problems.push({ field: `regions[${index}]`, message: "must be a region id (lowercase letters, digits and dashes)" });
      sound = false;
    }
  });
  return sound ? (value as string[]) : null;
}

/**
 * Reads the quality weights of one yield entry.
 *
 * @param value The weights as parsed.
 * @param path The path of the weights.
 * @param problems The list to add to.
 * @returns The weights, or null when they are not sound.
 */
function readWeights(value: unknown, path: string, problems: CropProblem[]): QualityWeights | null {
  if (!isRecord(value)) {
    problems.push({ field: path, message: "must be { poor, normal, fine }" });
    return null;
  }
  refuseUnknown(value, ["poor", "normal", "fine"], path, problems);
  let sound = true;
  for (const key of ["poor", "normal", "fine"] as const) {
    if (!isWhole(value[key], 0, 1000)) {
      problems.push({ field: `${path}.${key}`, message: "must be a whole weight from 0 to 1000" });
      sound = false;
    }
  }
  if (!sound) return null;
  const weights = { poor: value.poor as number, normal: value.normal as number, fine: value.fine as number };
  if (weights.poor + weights.normal + weights.fine === 0) {
    problems.push({ field: path, message: "the weights must not all be 0" });
    return null;
  }
  return weights;
}

/**
 * Reads the yield list.
 *
 * @param value The yield as parsed.
 * @param context What items exist.
 * @param problems The list to add to.
 * @returns The entries, or null when the list or any entry is not sound.
 */
export function readYield(value: unknown, context: CropContext, problems: CropProblem[]): CropYield[] | null {
  if (!Array.isArray(value) || value.length === 0) {
    problems.push({ field: "yield", message: "must be a list of at least one item the crop gives" });
    return null;
  }
  const entries: CropYield[] = [];
  let sound = true;
  value.forEach((entry: unknown, index) => {
    const path = `yield[${index}]`;
    if (!isRecord(entry)) {
      problems.push({ field: path, message: "must be { item, count, quality }" });
      sound = false;
      return;
    }
    refuseUnknown(entry, ["item", "count", "quality"], path, problems);
    const before = problems.length;
    checkItem(entry.item, "material", `${path}.item`, context, problems);
    const count = readRange(entry.count, `${path}.count`, 1, problems);
    const quality = readWeights(entry.quality, `${path}.quality`, problems);
    if (problems.length !== before || count === null || quality === null) sound = false;
    else entries.push({ item: entry.item as string, count, quality });
  });
  return sound ? entries : null;
}

/**
 * Reads the seed return.
 *
 * @param value The seed return as parsed.
 * @param problems The list to add to.
 * @returns The seed return, or null when it is not sound.
 */
export function readSeedReturn(value: unknown, problems: CropProblem[]): { chance: number; count: CountRange } | null {
  if (!isRecord(value)) {
    problems.push({ field: "seedReturn", message: "must be { chance, count }" });
    return null;
  }
  refuseUnknown(value, ["chance", "count"], "seedReturn", problems);
  const chanceSound = isWhole(value.chance, 0, 100);
  if (!chanceSound) problems.push({ field: "seedReturn.chance", message: "must be a whole percent from 0 to 100" });
  const count = readRange(value.count, "seedReturn.count", 0, problems);
  return chanceSound && count !== null ? { chance: value.chance as number, count } : null;
}

/**
 * Reads the seed box a crop may have.
 *
 * @param value The seed box as parsed, or undefined.
 * @param context What items exist.
 * @param problems The list to add to.
 * @returns The seed box, null when absent or unsound; `sound` tells which.
 */
export function readSeedBox(value: unknown, context: CropContext, problems: CropProblem[]): { box: CropDefinition["seedBox"]; sound: boolean } {
  if (value === undefined) return { box: undefined, sound: true };
  if (!isRecord(value)) {
    problems.push({ field: "seedBox", message: "must be { item, opensTo }" });
    return { box: undefined, sound: false };
  }
  refuseUnknown(value, ["item", "opensTo"], "seedBox", problems);
  const before = problems.length;
  checkItem(value.item, "consumable", "seedBox.item", context, problems);
  const opensTo = readRange(value.opensTo, "seedBox.opensTo", 1, problems);
  if (problems.length !== before || opensTo === null) return { box: undefined, sound: false };
  return { box: { item: value.item as string, opensTo }, sound: true };
}

/**
 * Reads the regrow stage a crop may have.
 *
 * @param value The regrow as parsed, or undefined.
 * @param ripe The index of the ripe stage, or null when it is not known.
 * @param problems The list to add to.
 * @returns The regrow, null when absent or unsound; `sound` tells which.
 */
export function readRegrow(value: unknown, ripe: number | null, problems: CropProblem[]): { regrow: CropDefinition["regrow"]; sound: boolean } {
  if (value === undefined) return { regrow: undefined, sound: true };
  if (!isRecord(value)) {
    problems.push({ field: "regrow", message: "must be { toStage }" });
    return { regrow: undefined, sound: false };
  }
  refuseUnknown(value, ["toStage"], "regrow", problems);
  const limit = ripe === null ? 0 : ripe - 1;
  if (!isWhole(value.toStage, 0, limit)) {
    problems.push({ field: "regrow.toStage", message: "must be a stage before the ripe stage" });
    return { regrow: undefined, sound: false };
  }
  return { regrow: { toStage: value.toStage }, sound: true };
}

